import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useTheme,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  AccessTime,
  BeachAccess,
  CheckCircle,
  Cancel,
  Event,
  EmojiEvents,
  CalendarMonth,
  ArrowForward
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useAuth } from '@/store/authStore';
import useEmployeeDashboardStore from '@/store/Employee/EmployeeDashboardStore';
import useAttendanceStore from '@/store/Employee/AttendanceStore';
import FeedWidget from '@/components/OrganizationFeed/FeedWidget';
import {
  DashboardHeader,
  StatTile,
  MiniCalendar,
  QuickActionsBar,
  getEmployeeQuickActions,
  WallOfFameWidget
} from '@/components/Dashboard';
import {
  getCompanySettings,
  getUpcomingHolidays,
  getMyRecognitions,
  getRecentRecognitions,
  getMyLeaves,
  getUpcomingBirthdays,
  CompanySettings,
  Holiday,
  Recognition,
  MyLeave,
  Birthday
} from '@/services/DashboardAnalyticsService';

const EmployeeDashboard: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    weekStats,
    loading,
    error,
    fetchWeekStats,
    resetError
  } = useEmployeeDashboardStore();

  const { todayAttendance, fetchTodayAttendance } = useAttendanceStore();
  const [liveTime, setLiveTime] = useState({ hours: 0, minutes: 0 });

  // New state for dashboard features
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [myLeaves, setMyLeaves] = useState<MyLeave[]>([]);
  const [birthdays, setBirthdays] = useState<Birthday[]>([]);
  const [myRecognitions, setMyRecognitions] = useState<Recognition[]>([]);
  const [wallOfFameRecognitions, setWallOfFameRecognitions] = useState<Recognition[]>([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Calculate leave balance (placeholder - would come from actual API)
  const leaveBalance = {
    total: 20,
    used: 8,
    remaining: 12
  };

  useEffect(() => {
    fetchWeekStats();
    fetchTodayAttendance();
  }, []);

  // Fetch new dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setAnalyticsLoading(true);
      try {
        const [settingsRes, holidaysRes, recognitionsRes, myLeavesRes, birthdaysRes, wallOfFameRes] = await Promise.all([
          getCompanySettings(),
          getUpcomingHolidays(5),
          getMyRecognitions(),
          getMyLeaves(),
          getUpcomingBirthdays(),
          getRecentRecognitions(4)
        ]);

        if (settingsRes.success && settingsRes.data) {
          setCompanySettings(settingsRes.data);
        }
        if (holidaysRes.success && holidaysRes.data) {
          setHolidays(holidaysRes.data);
        }
        if (recognitionsRes.success && recognitionsRes.data) {
          setMyRecognitions(recognitionsRes.data);
        }
        if (myLeavesRes.success && myLeavesRes.data) {
          setMyLeaves(myLeavesRes.data);
        }
        if (birthdaysRes.success && birthdaysRes.data) {
          setBirthdays(birthdaysRes.data);
        }
        if (wallOfFameRes.success && wallOfFameRes.data) {
          setWallOfFameRecognitions(wallOfFameRes.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Update live time
  useEffect(() => {
    const calculateLiveTime = () => {
      if (todayAttendance?.punch_in && !todayAttendance?.punch_out) {
        const punchIn = new Date(todayAttendance.punch_in);
        const now = new Date();
        const diff = now.getTime() - punchIn.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setLiveTime({ hours, minutes });
      } else if (todayAttendance?.total_hours) {
        const totalHours = Number(todayAttendance.total_hours);
        const hours = Math.floor(totalHours);
        const minutes = Math.round((totalHours - hours) * 60);
        setLiveTime({ hours, minutes });
      }
    };

    calculateLiveTime();
    const interval = setInterval(calculateLiveTime, 120000);
    return () => clearInterval(interval);
  }, [todayAttendance]);

  // Quick actions handlers
  const quickActionsHandlers = {
    onLogTime: () => navigate('/my-work'),
    onApplyLeave: () => navigate('/leave-portal'),
    onViewPayslip: () => navigate('/payslips'),
    onViewCalendar: () => navigate('/holidays')
  };

  const quickActions = getEmployeeQuickActions(quickActionsHandlers);

  // Get today's data with live time
  const getTodayData = () => {
    if (!weekStats) return null;
    const today = new Date().toISOString().split('T')[0];
    const todayData = weekStats.weekDays.find(d => d.date === today);
    if (!todayData) return null;

    if (todayAttendance?.punch_in && !todayAttendance?.punch_out) {
      return { ...todayData, hours: liveTime.hours + liveTime.minutes / 60, isLive: true };
    }
    return { ...todayData, isLive: false };
  };

  const todayData = getTodayData();
  const isPunchedIn = !!todayAttendance?.punch_in;
  const isPunchedOut = !!todayAttendance?.punch_out;

  // Helper to format date string without timezone issues
  // Takes "YYYY-MM-DD" and returns "Sun, Jan 26"
  const formatHolidayDate = (dateStr: string): string => {
    const cleanDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = cleanDate.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    // Create date at noon to avoid any timezone edge cases
    const date = new Date(year, month - 1, day, 12, 0, 0);
    return `${weekdays[date.getDay()]}, ${months[month - 1]} ${day}`;
  };

  // Helper to generate dates between start and end (inclusive)
  const getDatesBetween = (startDate: string, endDate: string): string[] => {
    const dates: string[] = [];
    const start = new Date(startDate + 'T12:00:00');
    const end = new Date(endDate + 'T12:00:00');

    while (start <= end) {
      const year = start.getFullYear();
      const month = String(start.getMonth() + 1).padStart(2, '0');
      const day = String(start.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      start.setDate(start.getDate() + 1);
    }
    return dates;
  };

  // Calendar events from holidays, team leaves, and birthdays
  const calendarEvents = [
    // Holidays
    ...holidays.map(h => ({
      date: h.date.includes('T') ? h.date.split('T')[0] : h.date,
      type: 'holiday' as const,
      title: h.name
    })),
    // My approved leaves - expand to each day in the leave period
    ...myLeaves.flatMap(leave => {
      const startDate = leave.start_date.includes('T') ? leave.start_date.split('T')[0] : leave.start_date;
      const endDate = leave.end_date.includes('T') ? leave.end_date.split('T')[0] : leave.end_date;
      const dates = getDatesBetween(startDate, endDate);
      return dates.map(date => ({
        date,
        type: 'leave' as const,
        title: leave.leave_type
      }));
    }),
    // Birthdays
    ...birthdays.map(b => ({
      date: b.birthday_this_year,
      type: 'birthday' as const,
      title: `${b.name}'s Birthday`
    }))
  ];

  const handleRefresh = () => {
    fetchWeekStats();
    fetchTodayAttendance();
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Dashboard Header */}
        <DashboardHeader
          companyName={companySettings?.company_name || 'Nucleus'}
          companyLogo={companySettings?.company_logo_url}
          showRefresh
          onRefresh={handleRefresh}
          loading={analyticsLoading}
        />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={resetError}>
            {error}
          </Alert>
        )}

        {/* Stats Tiles Row */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 2,
          mb: 4
        }}>
          <StatTile
            title="Attendance Status"
            value={isPunchedIn ? (isPunchedOut ? 'Done' : 'Active') : 'Pending'}
            icon={isPunchedIn ? <CheckCircle /> : <Cancel />}
            variant={isPunchedIn ? (isPunchedOut ? 'success' : 'info') : 'warning'}
            subtitle={isPunchedIn && todayAttendance?.punch_in
              ? `In: ${new Date(todayAttendance.punch_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}`
              : 'Not punched in'
            }
            onClick={() => navigate('/my-work')}
          />
          <StatTile
            title="Leave Balance"
            value={`${leaveBalance.remaining} days`}
            icon={<BeachAccess />}
            variant="warning"
            subtitle={`${leaveBalance.used} used of ${leaveBalance.total}`}
            onClick={() => navigate('/leave-portal')}
          />
          <StatTile
            title="Hours Today"
            value={`${liveTime.hours}h ${liveTime.minutes}m`}
            icon={<AccessTime />}
            variant="primary"
            subtitle={todayData?.isLive ? 'Live tracking' : 'Completed'}
            onClick={() => navigate('/attendance-history')}
          />
          <StatTile
            title="Recognitions"
            value={myRecognitions.length}
            icon={<EmojiEvents />}
            variant="info"
            subtitle="Badges earned"
          />
        </Box>

        {/* Main Content Grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
          gap: 3,
          mb: 4
        }}>
          {/* Left Column - Attendance & Weekly Stats */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Today's Punch Status Card */}
            <Card sx={{
              background: isPunchedIn
                ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.background.paper} 100%)`
                : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.background.paper} 100%)`,
              border: `2px solid ${isPunchedIn ? theme.palette.success.main : theme.palette.warning.main}`,
              borderRadius: 2
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {isPunchedIn ? (
                    <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main }} />
                  ) : (
                    <Cancel sx={{ fontSize: 40, color: theme.palette.warning.main }} />
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {isPunchedIn ? (isPunchedOut ? 'Day Completed' : 'Punched In') : 'Not Punched In'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Today's Status
                    </Typography>
                  </Box>
                  {todayData?.isLive && (
                    <Chip
                      label="LIVE"
                      size="small"
                      sx={{
                        ml: 'auto',
                        backgroundColor: theme.palette.success.main,
                        color: '#fff',
                        fontWeight: 600,
                        animation: 'pulse 1s infinite'
                      }}
                    />
                  )}
                </Box>

                {isPunchedIn && (
                  <Box sx={{
                    display: 'flex',
                    gap: 2,
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
                  }}>
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Punch In</Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {new Date(todayAttendance!.punch_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                      </Typography>
                    </Box>
                    {isPunchedOut && (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">Punch Out</Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary.main">
                            {new Date(todayAttendance!.punch_out!).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                          </Typography>
                        </Box>
                      </>
                    )}
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ flex: 1, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Duration</Typography>
                      <Typography variant="h6" fontWeight="bold" color="info.main">
                        {liveTime.hours}h {liveTime.minutes}m
                      </Typography>
                    </Box>
                  </Box>
                )}

                {!isPunchedIn && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate('/my-work')}
                    sx={{ mt: 1 }}
                  >
                    Go to Daily Log
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Weekly Hours Summary */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : weekStats && (
              <Card sx={{
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccessTime sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Last 5 Days
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        {Math.floor(weekStats.totalWeekHours)}h {Math.round((weekStats.totalWeekHours % 1) * 60)}m
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Hours
                      </Typography>
                    </Box>
                  </Box>

                  {/* Simple Bar Chart */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 150, mt: 2 }}>
                    {weekStats.weekDays.map((day) => {
                      const today = new Date().toISOString().split('T')[0];
                      const isToday = day.date === today;
                      const displayHours = isToday && todayData?.isLive ? todayData.hours : day.hours;
                      const maxHours = Math.max(...weekStats.weekDays.map(d => d.hours), 8);
                      const heightPercent = (displayHours / maxHours) * 100;

                      return (
                        <Box
                          key={day.date}
                          sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {displayHours > 0 ? `${Math.floor(displayHours)}h` : '-'}
                          </Typography>
                          <Box
                            sx={{
                              width: '100%',
                              height: `${heightPercent || 5}%`,
                              minHeight: 4,
                              backgroundColor: isToday
                                ? (todayData?.isLive ? theme.palette.success.main : theme.palette.secondary.main)
                                : (displayHours > 0 ? theme.palette.primary.main : theme.palette.grey[300]),
                              borderRadius: 1,
                              transition: 'all 0.3s'
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 1,
                              fontWeight: isToday ? 700 : 400,
                              color: isToday ? theme.palette.secondary.main : 'text.secondary'
                            }}
                          >
                            {day.dayShort}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* Right Column - Calendar & Holidays */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Mini Calendar */}
            <MiniCalendar
              events={calendarEvents}
              onDateClick={(date) => console.log('Clicked date:', date)}
            />

            {/* Upcoming Holidays */}
            <Card sx={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
              borderRadius: 2,
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Event sx={{ color: theme.palette.error.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Upcoming Holidays
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/holidays')}
                    sx={{ textTransform: 'none' }}
                  >
                    View All
                  </Button>
                </Box>

                <List sx={{ p: 0 }}>
                  {holidays.length > 0 ? holidays.slice(0, 4).map((holiday, index) => (
                    <React.Fragment key={holiday.id}>
                      <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <CalendarMonth sx={{ color: theme.palette.error.main }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={holiday.name}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondary={formatHolidayDate(holiday.date)}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <Chip
                          label={holiday.type}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            backgroundColor: holiday.type === 'public' ? '#F4433620' : '#FF980020',
                            color: holiday.type === 'public' ? '#F44336' : '#FF9800'
                          }}
                        />
                      </ListItem>
                      {index < Math.min(holidays.length, 4) - 1 && <Divider />}
                    </React.Fragment>
                  )) : (
                    <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body2">No upcoming holidays</Typography>
                    </Box>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* My Recognitions */}
            {myRecognitions.length > 0 && (
              <Card sx={{
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EmojiEvents sx={{ color: '#FFD700' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      My Recognitions
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {myRecognitions.slice(0, 4).map((recognition) => (
                      <Chip
                        key={recognition.id}
                        label={recognition.title}
                        size="small"
                        sx={{
                          backgroundColor: '#FFD70020',
                          color: theme.palette.text.primary,
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>

        {/* Organization Feed Widget */}
        <Box sx={{ mb: 4 }}>
          <FeedWidget limit={3} />
        </Box>

        {/* Wall of Fame Widget */}
        <Box sx={{ mb: 4 }}>
          <WallOfFameWidget
            recognitions={wallOfFameRecognitions}
            loading={analyticsLoading}
            canAddRecognition={false}
            onViewAll={() => navigate('/wall-of-fame')}
            maxItems={4}
          />
        </Box>

        {/* Quick Actions */}
        <QuickActionsBar actions={quickActions} variant="horizontal" />

        {/* CSS Animation */}
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
      </Container>
    </Layout>
  );
};

export default EmployeeDashboard;
